import React, { useEffect, useMemo, useState } from "react";
import useStyles from "../../../components/common/model/styles";
import Popup from "../../../components/common/model/Popup";
import closeIcon from "../../../assets/closeModel.png";
import checkedIcon from "../../../assets/check-mark.png";
import deleteIcon from "../../../assets/trash.png";
import { DialogContent, DialogContentText, Grid } from "@mui/material";
import Notification from "../../../components/common/notification/Notification";
import Validation from "../../../utils/Validation";
import FormField from "../../../components/common/FormField";
import { useDispatch } from "react-redux";
import { setNotificationAction } from "../../../store/reducers/mmymSlice";
import { formElementFields, validateComplaintForm } from "./complaintAllFields";
import ResetFormAlert from "../settings/manageReffer/ResetFormAlert";
import { getAllSubUserAction } from "../../../store/reducers/subUserSlice";
import { useSelector } from "react-redux";
import {
  addComplaintAction,
  getComplaintByIdAction,
} from "../../../store/reducers/complaintSlice";
import dayjs from "dayjs";
import { getCompleteWorkDiaryAction } from "../../../store/reducers/workDiarySlice";

interface ComplaintDetail {
  [key: string]: any;
  title: any | null;
  job_id: any | null;
  handled_by: any | null;
  reported_date: any | null;
  reported_by: any | null;
  priority: any | null;
  status: any | null;
  description: any | null;
  activeQuoteId?:any;
}

interface ManageComplaintProps {
  open: boolean;
  onHide: () => void;
  title: string;
  selectComplaint: any;
  isSmallScreen?: boolean;
  onSave?: (data: any) => void;
  activeQuoteId?:any;
}

const initialComplaintState: ComplaintDetail = {
  title: "",
  customer_name: "",
  quote_id: "",
  job_id: "",
  job_name: "",
  handled_by: "",
  reported_date: dayjs().format("YYYY-MM-DD"),
  reported_by: "Customer",
  priority: "General",
  status: "Initiated",
  description: "",
  lead_id: "",
};

const ManageComplaint = (props: ManageComplaintProps) => {
  const classes = useStyles();
  const { selectComplaint, onHide, open, title, activeQuoteId } = props;
  const dispatch = useDispatch();
  const { users } = useSelector((state: any) => state.subUser);
  const { userProfile } = useSelector((state: any) => state.user);
  const { leadDetails } = useSelector((state: any) => state.lead);
  const [complaintDetail, setComplaintDetail] = useState<ComplaintDetail>(
    initialComplaintState,
  );
  const [fieldErrors, setFieldErrors] = useState<any>({});
  const { completeWorkDiaryData } = useSelector(
    (state: any) => state?.workDiary,
  );
  const leadId =
    selectComplaint?.lead_id ||
    complaintDetail?.lead_id ||
    leadDetails?.lead?.generated_id ||
    "";

  const fieldsToRender = useMemo(() => {
    const userOptions = (users || []).map((user: any) => ({
      label: user.name,
      id: user.staff_id ?? user?.tenant_id,
    }));

    const allTasks = (completeWorkDiaryData || []).flatMap(
      (item: any) => item.tasks || [],
    );

    const uniqueJobOptions: Array<{ label: string; id: string }> = [];
    const seenJobIds = new Set<string>();

    allTasks.forEach((task: any) => {
      const id = String(task?.job_id ?? "").trim();
      const label = String(task?.job_title ?? "").trim();
      if (!id || !label || seenJobIds.has(id)) return;
      seenJobIds.add(id);
      uniqueJobOptions.push({ label, id });
    });

    return formElementFields.map((field) => {
      if (field.id === "handled_by") {
        return {
          ...field,
          options: userOptions,
        };
      }

      if (field.id === "job_id") {
        return {
          ...field,
          options: [{ label: "General", id: "General" }, ...uniqueJobOptions],
        };
      }

      return field;
    });
  }, [users, completeWorkDiaryData]);

  useEffect(() => {
    dispatch(getAllSubUserAction({ status: "ACTIVE" }));
    if (open && leadId) {
      dispatch(
        getCompleteWorkDiaryAction({
          search: "",
          startDateRange: "",
          endDateRange: "",
          taskType: "",
          leadId,
        }),
      );
    }
  }, [dispatch, open, leadId]);

  useEffect(() => {
    if (selectComplaint?.complaint_id) {
      dispatch(
        getComplaintByIdAction({ complaint_id: selectComplaint?.complaint_id }),
      );
    }
  }, [dispatch, selectComplaint?.complaint_id,open]);

  const [alertModelShow, setAlertModelShow] = useState(false);
  const [isClickReset, setIsClickReset] = useState(false);

  const [
    error,
    checkValidator,
    resetValidator,
    resetAllValidator,
    isError,
    isErrorOptional,
  ]: any = Validation({
    title: complaintDetail?.title,
    job: complaintDetail?.job_id || complaintDetail?.job_name,
  });

  const [isActionCalled, setIsActionCalled] = useState(false);

  useEffect(() => {
    if (selectComplaint) {
      const isGeneral =
        selectComplaint?.job_id === null &&
        selectComplaint?.job_name === "General";

      setComplaintDetail({
        complaint_id: selectComplaint?.complaint_id || "",
        title: selectComplaint?.title || "",
        job_id: isGeneral
          ? "General" // 
          : String(selectComplaint?.job_id || ""),
        job_name: selectComplaint?.job_name || "",
        quote_id:activeQuoteId?.quoteId || null,
        handled_by:
          selectComplaint?.handled_by ||
          userProfile?.staff_id ||
          userProfile?.tenant_id ||
          "",
          recovered:selectComplaint?.recovered || null,
        reported_date:
          selectComplaint?.reported_date || dayjs().format("YYYY-MM-DD"),
        reported_by: selectComplaint?.reported_by || "Customer",
        priority: selectComplaint?.priority || "General",
        status: selectComplaint?.status || "Initiated",
        description: selectComplaint?.description || "",
        customer_name: selectComplaint?.customer_name || "",
        lead_id: selectComplaint?.lead_id || "",
      });
    } else {
      setComplaintDetail({
        ...initialComplaintState,
        job_id: "General", // ✅ default
        job_name: "General",
        handled_by: userProfile?.staff_id || userProfile?.tenant_id || "",
        customer_name: leadDetails?.lead?.customer_name || "",
        lead_id: leadDetails?.lead?.generated_id || "",
        quote_id:activeQuoteId?.quoteId || null,
      });
    }
  }, [selectComplaint, leadDetails, activeQuoteId, userProfile?.staff_id, userProfile?.tenant_id]);

  const hideHandler = () => {
    onHide();
    handleClear();
    dispatch(setNotificationAction({ message: [], severity: "error" }));
  };

  const handleClear = () => {
    if (selectComplaint?.complaint_id) {
      const isGeneral =
        selectComplaint?.job_id === null &&
        selectComplaint?.job_name === "General";
      setComplaintDetail({
        complaint_id: selectComplaint?.complaint_id || "",
        title: selectComplaint?.title || "",
        job_id: isGeneral
          ? "General" // ✅ UI
          : String(selectComplaint?.job_id || ""),
        job_name: selectComplaint?.job_name || "",
        handled_by:
          selectComplaint?.handled_by ||
          userProfile?.staff_id ||
          userProfile?.tenant_id ||
          "",
        reported_date: selectComplaint?.reported_date || "",
        reported_by: selectComplaint?.reported_by || "",
        priority: selectComplaint?.priority || "",
        status: selectComplaint?.status || "",
        description: selectComplaint?.description || "",
      });
    } else {
      setComplaintDetail(initialComplaintState);
    }
    resetAllValidator();
    setIsClickReset(false);
    setAlertModelShow(false);
  };

  const openClearPopup = () => {
    setAlertModelShow(true);
  };

  useEffect(() => {
    if (isClickReset) {
      handleClear();
      setAlertModelShow(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isClickReset]);

  const handleSubmit = (event: any) => {
    event.preventDefault();

    const errors = validateComplaintForm(complaintDetail, formElementFields);

    setFieldErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    setIsActionCalled(true);
  };

  useEffect(() => {
    if (isActionCalled) {
      if (
        !isError() &&
        !isErrorOptional() &&
        error !== null &&
        isActionCalled
      ) {
        // On save, generate Complaint ID if not present
        let saveData = { ...complaintDetail };
        // Convert recovered to number if value exists
      if (saveData?.recovered !== undefined && saveData?.recovered !== "") {
        saveData.recovered = Number(saveData.recovered);
      }
        // Only save lead_id, not customer_name
        const { customer_name, ...toSave } = saveData;
        dispatch(addComplaintAction(toSave));
      }
      setIsActionCalled(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActionCalled]);

  const handleInputChange = (field: string, value: any) => {
    if (field === "job_id") {
      // find selected option
      const jobField = fieldsToRender.find((f) => f.id === "job_id");

      const selectedOption = (jobField?.options as any[])?.find(
        (opt: any) => opt.id === value,
      );

      // If General selected
      if (value === "General") {
        setComplaintDetail((prev) => ({
          ...prev,
          job_id: null, // ✅ null
          job_name: "General", // ✅ label
        }));
      } else {
        setComplaintDetail((prev) => ({
          ...prev,
          job_id: String(value), // keep select value type stable
          job_name: selectedOption?.label || "", // ✅ name
        }));
      }

      setFieldErrors((prev: any) => ({
        ...prev,
        [field]: false,
      }));

      return;
    }

    // Normal fields
    setComplaintDetail((prevState) => ({
      ...prevState,
      [field]: value,
    }));

    resetValidator(field);
  };

  const onBlurAction = (e: any) => {
    checkValidator(e.target.id);
  };

  return (
    <>
      <Popup
        open={open}
        title={title}
        onHide={hideHandler}
        fullWidth={true}
        maxWidth={"md"}
        aria-describedby="alert-dialog-slide-description"
        popupBg="#fff"
        header={
          <>
            <img
              onClick={(e: any) => handleSubmit(e)}
              src={checkedIcon}
              className={classes.checkicons}
              alt="save"
            />
            <img
              onClick={openClearPopup}
              src={deleteIcon}
              className={classes.deleteicons}
              alt="delete"
            />
            <img
              onClick={hideHandler}
              src={closeIcon}
              className={classes.closeicons}
              alt="close"
            />
          </>
        }
      >
        <DialogContent sx={{ padding: "16px 10px !important" }}>
          <DialogContentText>
            <>
              <Notification />
              <Grid
                container
                direction="row"
                spacing={0.5}
                rowGap={0}
                rowSpacing={0}
                sx={{
                  background: "#E5F4F1",
                  padding: "10px !important",
                  marginTop: "10px !important",
                  borderRadius: "5px",
                  border: "1px solid #CFCFCF",
                  overflow: "hidden",
                }}
              >
                {fieldsToRender.map((field, i) => {
                  return (
                    <FormField
                      key={field.id}
                      onPaste={(e) =>
                        field?.id === "title" ? "" : e.preventDefault()
                      }
                      onKeyDown={(event: any) => {
                        console.log(
                          "event log-",
                          event,
                          event.target.value + event.key,
                        );
                        if (
                          event.keyCode === 8 ||
                          event.keyCode === 9 ||
                          event.keyCode === 37 ||
                          event.keyCode === 38 ||
                          event.keyCode === 39 ||
                          event.keyCode === 40 ||
                          event.keyCode === 18 ||
                          event.keyCode === 17 ||
                          event.ctrlKey
                        )
                          return event;
                        if (
                          field?.patternAllowed &&
                          !field?.patternAllowed.test(event.key)
                        ) {
                          event.preventDefault();
                        } else if (
                          field?.patternAllowed &&
                          !field?.patternAllowed.test(
                            event.target.value + event.key,
                          )
                        ) {
                          event.preventDefault();
                        }
                      }}
                      {...{
                        ...field,
                        ...{
                          variant: "outlined",
                          inSingleGridForm: true,
                          isNonMarginTop: true,
                          InputProps: {
                            ...(field?.inputProps && field?.inputProps),
                            sx: {
                              height:
                                field?.id === "details" &&
                                complaintDetail[field?.id]?.length > 36
                                  ? "auto"
                                  : field?.type === "textarea"
                                    ? "auto"
                                    : "36px",
                              padding:
                                field?.id === "details" ||
                                field?.type === "textarea"
                                  ? "4px"
                                  : "",
                            },
                            autocomplete: "complaint-new-field",
                          },
                          value: complaintDetail
                            ? complaintDetail[field.id]
                            : "",
                          onBlur: onBlurAction,
                          onChange: (e: any) =>
                            handleInputChange(field.id, e.target.value),
                          error: fieldErrors[field.id] || false,
                        },
                      }}
                    />
                  );
                })}
              </Grid>
            </>
          </DialogContentText>
        </DialogContent>
      </Popup>
      {alertModelShow && (
        <ResetFormAlert
          open={alertModelShow}
          title=""
          alertModalShow={alertModelShow}
          setAlertModelShow={setAlertModelShow}
          setIsClickReset={setIsClickReset}
        />
      )}
    </>
  );
};

export default ManageComplaint;
